import React, { Fragment } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { formatCompetitionPeriod } from './helpers';
import { DetailRowProps, ICompetitionDetailsList } from './interfaces';
import { useTranslation } from 'react-i18next';
import { WeaponGroupChips } from '@components/competitions/WeaponGroupChips';

const iconSx = { fontSize: '1.2rem' };

const DetailRow: React.FC<DetailRowProps> = ({
  icon,
  alignItems = 'center',
  children,
}): JSX.Element => {
  return (
    <Stack direction="row" spacing={1} alignItems={alignItems}>
      <Box
        sx={{
          display: 'flex',
          alignItems: alignItems === 'flex-start' ? 'flex-start' : 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Stack>
  );
};

export const CompetitionDetailsList: React.FC<ICompetitionDetailsList> = ({
  startDate,
  endDate,
  statusName,
  participants,
  formatName,
  weaponGroups = [],
}): JSX.Element => {
  const { t } = useTranslation();
  const { start, end } = formatCompetitionPeriod(startDate, endDate);
  const period = t('competitions:portal.details.periodValue', {
    start,
    end,
  });

  return (
    <Stack spacing={1.25}>
      <DetailRow icon={<CalendarMonthIcon color="primary" sx={iconSx} />}>
        <Typography variant="body2" fontSize=".8rem">
          <b>{t('competitions:portal.details.period')}:</b> {period}
        </Typography>
      </DetailRow>

      <DetailRow icon={<GroupWorkIcon color="primary" sx={iconSx} />}>
        <Typography variant="body2" fontSize=".8rem">
          <b>{t('competitions:entities.competition.format')}:</b> {formatName}
        </Typography>
      </DetailRow>

      <DetailRow icon={<AccessTimeIcon color="primary" sx={iconSx} />}>
        <Typography variant="body2" fontSize=".8rem">
          <b>Status:</b> {statusName}
        </Typography>
      </DetailRow>

      <DetailRow icon={<PeopleIcon color="primary" sx={iconSx} />}>
        <Typography variant="body2" fontSize=".8rem">
          <b>Deelnemers</b>: {participants}
        </Typography>
      </DetailRow>

      {weaponGroups.length > 0 && (
        <DetailRow
          icon={<GpsFixedIcon color="primary" sx={iconSx} />}
          alignItems="flex-start"
        >
          <Fragment>
            <Typography variant="body2" fontSize=".8rem" sx={{ mb: 0.5 }}>
              <b>{t('competitions:entities.discipline.weaponGroups')}:</b>
            </Typography>
            <WeaponGroupChips weaponGroups={weaponGroups} />
          </Fragment>
        </DetailRow>
      )}
    </Stack>
  );
};
